import React from "react";
import { loadPuzzleForDate } from "../../lib/puzzle-service";
import { getToday } from "../../lib/time-utils";
import { DEFAULT_SPORT, SPORT_LABELS, SUPPORTED_SPORTS } from "../../lib/constants";

export const PuzzleDataContext = React.createContext();

const SPORT_STORAGE_KEY = "selectedSport";

const getInitialSport = () => {
    try {
        const saved = localStorage.getItem(SPORT_STORAGE_KEY);
        if (saved && SUPPORTED_SPORTS.includes(saved)) {
            return saved;
        }
    } catch (e) {
        // localStorage not available
    }
    return DEFAULT_SPORT;
};

function PuzzleDataProvider({ children }) {
    const [sport, setSport] = React.useState(getInitialSport);
    const [puzzleDate, setPuzzleDate] = React.useState(getToday());
    const [puzzleMeta, setPuzzleMeta] = React.useState({
        puzzleId: "",
        source: "local",
        sequence: null,
        noPuzzle: false,
    });
    const [gameData, setGameData] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    const refreshPuzzle = React.useCallback(async (nextSport) => {
        setIsLoading(true);
        const today = getToday();
        const puzzle = await loadPuzzleForDate({
            sport: nextSport,
            puzzleDate: today,
        });
        setGameData(puzzle.gameData);
        setPuzzleMeta({
            puzzleId: puzzle.puzzleId,
            source: puzzle.source,
            sequence: puzzle.sequence,
            noPuzzle: puzzle.noPuzzle ?? false,
        });
        // Parse 'YYYY-MM-DD' as local time to avoid UTC conversion shifts
        const [y, m, d] = puzzle.puzzleDate.split('-').map(Number);
        setPuzzleDate(new Date(y, m - 1, d));
        setError(puzzle.error ?? null);
        setIsLoading(false);
    }, []);

    React.useEffect(() => {
        refreshPuzzle(sport);
    }, [refreshPuzzle, sport]);

    React.useEffect(() => {
        document.title = `${SPORT_LABELS[sport]} Connections`;
        // Persist sport selection to localStorage
        try {
            localStorage.setItem(SPORT_STORAGE_KEY, sport);
        } catch (e) {
            // localStorage not available
        }
    }, [sport]);

    const categorySize = gameData[0]?.words?.length ?? 4;
    const numCategories = gameData.length;

    return (
        <PuzzleDataContext.Provider
            value={{
                gameData,
                numCategories,
                categorySize,
                sport,
                setSport,
                puzzleDate,
                puzzleId: puzzleMeta.puzzleId,
                puzzleSequence: puzzleMeta.sequence,
                puzzleSource: puzzleMeta.source,
                noPuzzle: puzzleMeta.noPuzzle,
                isLoading,
                error,
                refreshPuzzle,
            }}
        >
            {children}
        </PuzzleDataContext.Provider>
    );
}

export default PuzzleDataProvider;
